<template>
  <div
    v-if="curatedDeal !== null"
    class="tiled-deal">
    <div class="header">
      <green-score
        v-if="curatedDeal.co2EmissionsScore > 0"
        class="green-score"
        :is-animating="false"
        :show-green-leaf="false"
        :co2-score="curatedDeal.co2EmissionsScore" />
      <div class="deal-name">
        <router-link :to="curatedDeal.endpoint">
          {{ curatedDeal.name }}
        </router-link>
      </div>
      <div
        v-if="isDemandOrganization"
        class="buttons">
        <button
          name="buyPackage"
          :disabled="!permissions.canWriteLineitem || !canSelectItem"
          :data-entity-type="curatedDeal.name"
          class="btn btn-xs"
          :class="isSelected ? 'btn-danger-hollow' : 'btn-default-hollow'"
          @click="$emit('selectionChanged', curatedDeal)">
          <component
            :is="!isSelected ? 'icon-add-to-cart' : 'icon-x'"
            class="m-0"
            style="width: 2rem; height: 1.75rem" />
        </button>
      </div>
    </div>
    <div
      v-if="dealCurator !== null"
      class="section curator-logo">
      <img
        :key="dealCurator.curatorOrganizationID"
        :src="dealCurator.logoURL"
        :alt="dealCurator.name" />
    </div>
    <div class="deal-id">
      <deal-id
        :deal-i-d="curatedDeal.dealIDFormatted"
        :show-copy-button="false"
        :show-icon="true"
        :small="true" />
    </div>
    <div class="section"></div>
    <div class="section kpis two">
      <div class="kpi">
        <div class="value money">
          {{ getValueOrDash(curatedDeal.totalCuratedDeliveryStatistics?.dailyImpressionsFormatted) }}
        </div>
        <div class="title">{{ $t('messages.dailyImpressions') }}</div>
      </div>
      <div class="kpi">
        <div class="value money">
          {{ curatedDeal.totalCuratedCPMFormatted }}
        </div>
        <div class="title">{{ $t('names.eCPM', { n: 1 }) }}</div>
      </div>
    </div>
    <div class="section kpis two">
      <div class="kpi">
        <div class="value">
          {{ getValueOrDash(curatedDeal.totalCuratedDeliveryStatistics?.siteCountFormatted) }}
        </div>
        <div class="title">{{ $t('messages.siteCount') }}</div>
      </div>
      <div class="kpi">
        <div class="value">
          {{ getValueOrDash(curatedDeal.totalCuratedDeliveryStatistics?.unitCountFormatted) }}
        </div>
        <div class="title">{{ $t('names.screenCount', { n: 1 }) }}</div>
      </div>
    </div>
    <div class="section">
      <div class="label">{{ capitalize($t('names.date', { n: 2 })) }}</div>
      <div class="value">
        <p>{{ curatedDeal.datesDescription }}</p>
      </div>
    </div>
  </div>
</template>

<script>
  import { capitalize } from 'vue';
  import { mapGetters, mapState } from 'vuex';

  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import { IconAddToCart, IconX } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import { getValueOrDash } from '@/globals';
  import isLiveboard from '@/utils/IsLiveboard';
  import i18n from '@/vueI18n';

  import DealId from './DealID';

  export default {
    components: {
      GreenScore,
      DealId,
      IconAddToCart,
      IconX,
    },
    props: {
      curatedDeal: {
        type: Package,
        required: true,
      },
      allDemographics: {
        type: Array,
        required: true,
      },
      allMediaOwners: {
        type: Array,
        required: true,
      },
      allCurators: {
        type: Array,
        required: true,
      },
      buyerOrganizationDSPs: {
        type: Array,
        required: true,
      },
      isSelected: {
        type: Boolean,
        default: false,
      },
      canSelectItem: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['selectionChanged'],
    computed: {
      ...mapGetters(['permissions']),
      ...mapGetters('orgApp', ['isDemandOrganization']),
      ...mapState('orgApp', {
        organizationExternalExchanges: (state) => state.ReferenceData.organizationExternalExchanges || [],
      }),
      mediaOwners() {
        // Curated deals are always coming from Hivestack / LIVE BOARD for now.
        return [
          {
            id: -1,
            name: isLiveboard() ? 'LIVE BOARD' : 'Hivestack',
            logoUrl: isLiveboard()
              ? '/assets/images/liveboard-logo-dark.svg'
              : '/assets/images/hivestack-logo-colored-largefont.svg',
          },
        ];
      },
      dealCurator() {
        return this.allCurators !== undefined
          ? this.allCurators.find((x) => x.curatorOrganizationID === this.curatedDeal.organizationID)
          : null;
      },
      mobileRetargetingTooltip() {
        return capitalize(i18n.global.t('messages.mobileRetargetingEnabled'));
      },
      // greennessScoreTooltip() {
      //   return i18n.global.t('messages.greennessScoreDescription', ['<b>CO<sub>2</sub>e</b>']);
      // },
    },
    methods: {
      getValueOrDash,
      capitalize(text) {
        return capitalize(text);
      },
      getDemographicsDescription(ids) {
        if (ids.length > 0) {
          return ids
            .map((id) =>
              this.allDemographics.find((d) => d.id === id) !== undefined
                ? this.allDemographics.find((d) => d.id === id).name
                : capitalize(i18n.global.t('names.customAudience', { n: 1 })),
            )
            .join(', ');
        }
        return capitalize(i18n.global.t('messages.allAudiences', { n: 1 }));
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .tiled-deal {
    box-shadow: var(--panesShadow);
    background: var(--panesBackgroundColor);

    margin-bottom: 2.8rem;
    padding-bottom: 0.8rem;
    position: relative;

    .header {
      height: 7.4rem;
      min-height: 7.4rem;
      max-height: 7.4rem;

      background-color: var(--paneHeaderBackgroundColor);
      color: var(--textColorDark);

      padding: 1.6rem 1.6rem;
      font-weight: 700;
      font-size: 1em;
      display: flex;
      align-items: center;

      .green-score {
        width: 4.8rem !important;
        height: 4.2rem !important;
        font-size: 1.25rem;
        margin-right: 0.75rem;
        font-weight: lighter !important;
      }

      .icon {
        border: 0.1rem solid var(--iconColor);
        background: var(--iconColorReverse);

        border-radius: 3.2rem;
        height: 2.8rem;
        min-width: 2.8rem;
        margin-right: 0.8rem;
        display: flex;
        align-items: center;

        svg {
          margin: auto;
          display: block;
          height: 1.6rem;
          width: 1.6rem;
        }
      }

      .phone-icon {
        background: var(--success);

        border-radius: 3.2rem;
        height: 2.8rem;
        width: 2.8rem;
        margin-right: 0.8rem;
        display: flex;
        align-items: center;

        svg {
          margin: auto;
          display: block;
          width: 1.5rem;
          height: 1.5rem;
          stroke-width: 0.2rem;

          stroke: var(--successReverse);
        }
      }

      .deal-name {
        padding-right: 0.6rem;
        flex-grow: 1;
        word-break: break-all;
        display: flex;
        align-items: center;
        margin-right: 1.5rem;

        .green-score {
          width: 4rem;
          height: 4rem;
          font-size: 1.1rem;
          margin-right: 0.3rem;

          &:nth-child(2) {
            margin-right: 1rem;
          }
        }

        svg {
          width: 1.6rem;
          height: 1.6rem;
          position: relative;
          top: 0.1rem;
          margin-left: 0.75rem;
        }
      }

      .buttons {
        button.btn.btn-xs {
          font-size: 1.2rem !important;
        }
      }
    }

    .section {
      padding: 1.6rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      margin: 0 0.8rem;
      font-size: 1.4rem;
      flex-wrap: wrap;

      &:not(.kpis) {
        flex-direction: column-reverse;
        align-items: flex-start;
      }

      &:nth-of-type(odd) {
        background: #f7f7f7;
      }

      > .label {
        text-transform: uppercase;
        flex-basis: 40%;
        font-weight: 400;
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.3);
      }

      > .value {
        width: 100%;
        flex-basis: 100%;
        flex-grow: 1;
        text-align: left;
        min-width: 0;

        p {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          margin-bottom: 0;
        }
      }

      > .values {
        width: 100%;
        flex-basis: 100%;
        text-align: right;
        min-width: 0;

        ul {
          margin-bottom: 0;
          padding-left: 1.5rem;
        }
      }

      &.curator-logo {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2.4rem;
        height: 9.6rem;

        > img {
          margin: auto;
          max-height: 100%;
          max-width: 100%;
        }
      }
    }

    .deal-media-owner-logos {
      display: flex;
      align-items: center;
      margin: 2.4rem 1.6rem 1.2rem;
      gap: 1.6rem;
      flex-wrap: wrap;
      border: solid 1px #eee;
      padding: 1.6rem 1.2rem;
      position: relative;

      img {
        margin: auto;
        max-height: 1.5rem;
        max-width: 100%;
      }

      .badge {
        position: absolute;
        top: -1.3rem;
        left: 0.6rem;
      }
    }

    .kpis {
      display: flex !important;
      padding: 1.6rem 5rem;
      justify-content: center;

      &.four {
        .kpi {
          flex: 1 1 calc(25% - 1.6rem);
        }
      }

      &.three {
        .kpi {
          flex: 1 1 calc(33.3% - 1.6rem);
        }
      }

      &.two {
        .kpi {
          flex: 1 1 calc(50% - 1.6rem);
        }
      }

      .kpi {
        padding: 0.5rem 0;
        .value {
          font-weight: 700;
          text-align: center;
          font-size: 1.4rem;
        }

        .title {
          color: var(--textMutedColor);

          text-align: center;
          text-transform: uppercase;
          font-weight: 400;
          font-size: 1.1rem;
        }
      }
    }

    .deal-id {
      padding: 0 1.6rem;
      display: flex;
    }
  }
</style>
